import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSearch, faChevronUp, faChevronDown, faEllipsis, faPenToSquare, faCircleMinus, faGripVertical, faXmark, faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../../components/InputField";
import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import { getAllAgreementServices, deleteAgreementService, updateCategorySortOrder, updateServiceSortOrder, getAgreementsForService, getServiceCoverage, getAgreements } from "../../api/Agreement";
import useExporter from "../../hooks/useExporter";
import CustomDropdown from "../../components/CustomDropdown";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableRow = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      {children(listeners)}
    </tr>
  );
};

const AgreementServicesPage = () => {
  const { getAccessToken } = useAuth();
  const { handleExport } = useExporter();
  const navigate = useNavigate();
  const [agreementServices, setAgreementServices] = useState([]);
  const [search, setSearch] = useState("");
  const [agreementServicesLoaded, setAgreementServicesLoaded] = useState(false);

  // Drill-in: which agreements have a given service
  const [drawerService, setDrawerService] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerAgreements, setDrawerAgreements] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerSearch, setDrawerSearch] = useState("");

  const openServiceDrawer = async (service) => {
    setDrawerService(service);
    setDrawerSearch("");
    setDrawerAgreements([]);
    setDrawerLoading(true);
    // Mount off-screen first, then flip to open on the next tick so the
    // transform transition actually animates in.
    setTimeout(() => setDrawerOpen(true), 10);
    try {
      const accessToken = await getAccessToken();
      const data = await getAgreementsForService(service.agreementServicePublicId, accessToken);
      setDrawerAgreements(data);
    } catch (error) {
      toast.error("Failed to load agreements for service");
    } finally {
      setDrawerLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    // Unmount after the slide-out transition finishes.
    setTimeout(() => setDrawerService(null), 300);
  };

  const drawerFilteredAgreements = drawerSearch
    ? drawerAgreements.filter((a) =>
        a.agreementName?.toLowerCase().includes(drawerSearch.toLowerCase())
      )
    : drawerAgreements;

  // Excel coverage matrix: rows = agreements, columns = services (by category),
  // cell = "X" when the agreement has the service. Ignores the on-page search.
  const handleExportMatrix = async () => {
    const toastId = toast.loading("Building coverage matrix...");
    try {
      const accessToken = await getAccessToken();
      const [agreements, pairs] = await Promise.all([
        getAgreements(accessToken),
        getServiceCoverage(accessToken),
      ]);

      const orderedServices = [...agreementServices].sort(
        (a, b) =>
          (a.categorySortOrder ?? 0) - (b.categorySortOrder ?? 0) ||
          (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
          (a.name || "").localeCompare(b.name || "")
      );

      const pairSet = new Set(
        pairs.map((p) => `${p.agreementPublicId}|${p.agreementServicePublicId}`)
      );

      const rows = [...agreements]
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        .map((agreement) => {
          const row = { Agreement: agreement.name };
          orderedServices.forEach((service) => {
            row[service.name] = pairSet.has(
              `${agreement.agreementPublicId}|${service.agreementServicePublicId}`
            )
              ? "X"
              : "";
          });
          return row;
        });

      const currentDate = new Date().toISOString().split("T")[0];
      handleExport({ "Service coverage": rows }, `ServiceCoverage_${currentDate}`);
      toast.success("Coverage matrix exported", { id: toastId });
    } catch (error) {
      console.error("Failed to export coverage matrix:", error);
      toast.error("Failed to export coverage matrix", { id: toastId });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const accessToken = await getAccessToken();
      const data = await getAllAgreementServices(accessToken);
      setAgreementServices(data);
      setAgreementServicesLoaded(true);
    } catch (error) {
      console.error(error);
      setAgreementServicesLoaded(true);
    }
  };

  const filtered = search
    ? agreementServices.filter(
        (s) =>
          s.name?.toLowerCase().includes(search.toLowerCase()) ||
          s.category?.toLowerCase().includes(search.toLowerCase())
      )
    : agreementServices;

  const categories = useMemo(() => {
    const grouped = filtered.reduce((acc, service) => {
      const cat = service.category || "Uncategorized";
      if (!acc[cat]) {
        acc[cat] = {
          name: cat,
          publicId: service.categoryPublicId,
          sortOrder: service.categorySortOrder,
          items: [],
        };
      }
      acc[cat].items.push(service);
      return acc;
    }, {});
    return Object.values(grouped)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(cat => ({ ...cat, items: [...cat.items].sort((a, b) => a.sortOrder - b.sortOrder) }));
  }, [filtered]);

  const moveCategoryUp = async (catIndex) => {
    if (catIndex === 0) return;
    const updated = [...categories];
    const temp = updated[catIndex].sortOrder;
    updated[catIndex].sortOrder = updated[catIndex - 1].sortOrder;
    updated[catIndex - 1].sortOrder = temp;

    try {
      const accessToken = await getAccessToken();
      await updateCategorySortOrder(
        updated.filter((c) => c.publicId).map((c) => ({ publicId: c.publicId, sortOrder: c.sortOrder })),
        accessToken
      );
      await fetchServices();
    } catch (error) {
      toast.error("Failed to update category order");
    }
  };

  const moveCategoryDown = async (catIndex) => {
    if (catIndex >= categories.length - 1) return;
    const updated = [...categories];
    const temp = updated[catIndex].sortOrder;
    updated[catIndex].sortOrder = updated[catIndex + 1].sortOrder;
    updated[catIndex + 1].sortOrder = temp;

    try {
      const accessToken = await getAccessToken();
      await updateCategorySortOrder(
        updated.filter((c) => c.publicId).map((c) => ({ publicId: c.publicId, sortOrder: c.sortOrder })),
        accessToken
      );
      await fetchServices();
    } catch (error) {
      toast.error("Failed to update category order");
    }
  };

  const moveItemUp = async (catIndex, itemIndex) => {
    if (itemIndex === 0) return;
    const items = categories[catIndex].items;
    const updated = [
      { publicId: items[itemIndex].agreementServicePublicId, sortOrder: items[itemIndex - 1].sortOrder },
      { publicId: items[itemIndex - 1].agreementServicePublicId, sortOrder: items[itemIndex].sortOrder },
    ];

    try {
      const accessToken = await getAccessToken();
      await updateServiceSortOrder(updated, accessToken);
      await fetchServices();
    } catch (error) {
      toast.error("Failed to update service order");
    }
  };

  const moveItemDown = async (catIndex, itemIndex) => {
    const items = categories[catIndex].items;
    if (itemIndex >= items.length - 1) return;
    const updated = [
      { publicId: items[itemIndex].agreementServicePublicId, sortOrder: items[itemIndex + 1].sortOrder },
      { publicId: items[itemIndex + 1].agreementServicePublicId, sortOrder: items[itemIndex].sortOrder },
    ];

    try {
      const accessToken = await getAccessToken();
      await updateServiceSortOrder(updated, accessToken);
      await fetchServices();
    } catch (error) {
      toast.error("Failed to update service order");
    }
  };

  const handleDragEnd = async (event, catIndex) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const items = categories[catIndex].items;
    const oldIndex = items.findIndex((s) => s.agreementServicePublicId === active.id);
    const newIndex = items.findIndex((s) => s.agreementServicePublicId === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder and assign new sort values
    const reordered = [...items];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    const updates = reordered.map((item, idx) => ({
      publicId: item.agreementServicePublicId,
      sortOrder: idx + 1,
    }));

    // Optimistic update — reorder local state immediately
    const sortMap = Object.fromEntries(updates.map(u => [u.publicId, u.sortOrder]));
    setAgreementServices(prev =>
      prev.map(s =>
        sortMap[s.agreementServicePublicId] !== undefined
          ? { ...s, sortOrder: sortMap[s.agreementServicePublicId] }
          : s
      )
    );

    try {
      const accessToken = await getAccessToken();
      await updateServiceSortOrder(updates, accessToken);
    } catch (error) {
      toast.error("Failed to update service order");
      await fetchServices(); // Revert on error
    }
  };

  const handleDeleteService = async (publicId) => {
    const toastId = toast.loading("Deleting service...");
    try {
      const accessToken = await getAccessToken();
      await deleteAgreementService(publicId, accessToken);
      await fetchServices();
      toast.success("Service deleted", {
        id: toastId,
        iconTheme: { primary: "#4CAF50", secondary: "#DFF2BF" },
      });
    } catch (error) {
      toast.error("Failed to delete service", { id: toastId });
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"Agreement services"} loading={!agreementServicesLoaded} />
            </div>

            <div className={`${!agreementServicesLoaded ? "loading" : ""} page-content`}>
              <Breadcrumbs />
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">Agreement services</h2>
                <Link to={`/agreement-service/create`} className="btn-primary">
                  <FontAwesomeIcon icon={faPlus} className="mr-2 text-lg" />
                  Create agreement service
                </Link>
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-4 justify-end items-center gap-4">
                  <button
                    className="btn-outline-primary-small"
                    onClick={handleExportMatrix}
                  >
                    Export coverage matrix
                  </button>
                  <InputField
                    iconLeft={false}
                    icon={faSearch}
                    placeholder={"Search services..."}
                    value={search}
                    onChange={(v) => setSearch(v.target.value)}
                  />
                </div>

                <table className="table-default w-full">
                  <thead>
                    <tr>
                      <th className="w-8"></th>
                      <th className="w-12"></th>
                      <th className="text-left">Name</th>
                      <th className="text-left">Category</th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  {categories.map((cat, catIndex) => (
                    <React.Fragment key={cat.name}>
                      <tbody>
                        <tr className="bg-gray-100">
                          <td colSpan={5} className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="flex flex-col">
                                <button
                                  className={`text-xs ${catIndex === 0 ? "text-gray-300" : "text-gray-500 hover:text-black"}`}
                                  onClick={() => moveCategoryUp(catIndex)}
                                  disabled={catIndex === 0}
                                >
                                  <FontAwesomeIcon icon={faChevronUp} />
                                </button>
                                <button
                                  className={`text-xs ${catIndex >= categories.length - 1 ? "text-gray-300" : "text-gray-500 hover:text-black"}`}
                                  onClick={() => moveCategoryDown(catIndex)}
                                  disabled={catIndex >= categories.length - 1}
                                >
                                  <FontAwesomeIcon icon={faChevronDown} />
                                </button>
                              </div>
                              <span className="font-semibold text-sm">{cat.name}</span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(event) => handleDragEnd(event, catIndex)}
                      >
                        <SortableContext
                          items={cat.items.map((s) => s.agreementServicePublicId)}
                          strategy={verticalListSortingStrategy}
                        >
                          <tbody>
                            {cat.items.map((service, itemIndex) => (
                              <SortableRow key={service.agreementServicePublicId} id={service.agreementServicePublicId}>
                                {(listeners) => (
                                  <>
                                    <td className="cursor-grab" {...listeners}>
                                      <FontAwesomeIcon icon={faGripVertical} className="text-gray-400" />
                                    </td>
                                    <td>
                                      <div className="flex flex-col">
                                        <button
                                          className={`text-xs ${itemIndex === 0 ? "text-gray-300" : "text-gray-400 hover:text-black"}`}
                                          onClick={() => moveItemUp(catIndex, itemIndex)}
                                          disabled={itemIndex === 0}
                                        >
                                          <FontAwesomeIcon icon={faChevronUp} />
                                        </button>
                                        <button
                                          className={`text-xs ${itemIndex >= cat.items.length - 1 ? "text-gray-300" : "text-gray-400 hover:text-black"}`}
                                          onClick={() => moveItemDown(catIndex, itemIndex)}
                                          disabled={itemIndex >= cat.items.length - 1}
                                        >
                                          <FontAwesomeIcon icon={faChevronDown} />
                                        </button>
                                      </div>
                                    </td>
                                    <td>
                                      <button
                                        onClick={() => openServiceDrawer(service)}
                                        className="text-primary-600 hover:underline text-left"
                                      >
                                        {service.name}
                                      </button>
                                      <span
                                        className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                                        title="Agreements with this service"
                                      >
                                        {service.agreementCount ?? 0}
                                      </span>
                                      {service.websiteUrl && (
                                        <a
                                          href={service.websiteUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          aria-label={`Open ${service.name} website`}
                                          title="Open website"
                                          className="ml-2 text-primary-700 hover:text-primary-800"
                                        >
                                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                        </a>
                                      )}
                                    </td>
                                    <td>{service.category}</td>
                                    <td>
                                      <CustomDropdown
                                        isRight={true}
                                        ContentComponent={() => (
                                          <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                                            <button
                                              className="block px-4 py-2 text-sm hover:bg-gray-200 w-full text-left rounded-lg"
                                              onClick={() => navigate(`/agreement-service/${service.agreementServicePublicId}`)}
                                            >
                                              <FontAwesomeIcon icon={faPenToSquare} className="mr-4" />
                                              Edit
                                            </button>
                                            <button
                                              className="block px-4 py-2 text-sm text-red-500 hover:bg-gray-200 w-full text-left rounded-lg"
                                              onClick={() => handleDeleteService(service.agreementServicePublicId)}
                                            >
                                              <FontAwesomeIcon icon={faCircleMinus} className="mr-4" />
                                              Delete
                                            </button>
                                          </div>
                                        )}
                                      >
                                        <div className="w-8 h-6 flex justify-end items-center cursor-pointer">
                                          <FontAwesomeIcon icon={faEllipsis} />
                                        </div>
                                      </CustomDropdown>
                                    </td>
                                  </>
                                )}
                              </SortableRow>
                            ))}
                          </tbody>
                        </SortableContext>
                      </DndContext>
                    </React.Fragment>
                  ))}
                </table>
              </div>
            </div>
          </main>
        </div>
      </div>

      {drawerService && (
        <>
          <div
            className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${drawerOpen ? "opacity-100" : "opacity-0"}`}
            onClick={closeDrawer}
          />
          <div
            className={`fixed inset-y-0 right-0 w-[420px] max-w-full bg-white shadow-xl z-50 flex flex-col transition-transform duration-300 ease-out ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
          >
            <div className="flex items-start justify-between p-4 border-b">
              <div>
                <h3 className="font-semibold text-lg">{drawerService.name}</h3>
                <p className="text-sm text-gray-500">
                  {drawerLoading ? "Loading…" : `${drawerAgreements.length} agreements`}
                </p>
              </div>
              <button onClick={closeDrawer} className="text-gray-500 hover:text-black p-1">
                <FontAwesomeIcon icon={faXmark} className="text-lg" />
              </button>
            </div>

            <div className="p-4 border-b">
              <InputField
                iconLeft={false}
                icon={faSearch}
                placeholder={"Search agreements..."}
                value={drawerSearch}
                onChange={(v) => setDrawerSearch(v.target.value)}
              />
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {drawerLoading ? (
                <p className="p-4 text-sm text-gray-500">Loading…</p>
              ) : drawerAgreements.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">No agreements have this service.</p>
              ) : drawerFilteredAgreements.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">No agreements match your search.</p>
              ) : (
                drawerFilteredAgreements.map((a) => (
                  <Link
                    key={a.agreementPublicId}
                    to={`/${a.agreementPublicId}?tab=services`}
                    className="block px-3 py-2 rounded hover:bg-gray-100 text-sm text-primary-600"
                  >
                    {a.agreementName}
                  </Link>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AgreementServicesPage;
